import React, { useMemo } from 'react';

import { capitalize } from '@utils/formatting';

import MyTIReadingStat from './ReadingStat';
import type { MyTIReadingStatsProps } from './interfaces';

const MyTIReadingStats = ({
  articlesRead,
  minutesRead,
  mostReadTopic,
  mostReadNewsletters,
  newsletterNames,
}: MyTIReadingStatsProps) => {
  /**
   * Data transformation to get the most read newsletter
   * Data comes back in the form of ['newsletter_id: count', ...], so
   * we need to get the largest count and that newsletter's name
   */
  const mostReadNewsletter = useMemo(() => {
    return mostReadNewsletters?.reduce(
      (acc, pair) => {
        const [newsletterId, count] = pair.split(': ');

        if (acc.count < Number(count)) {
          acc.count = Number(count);
          acc.newsletter = newsletterNames?.[newsletterId] || '';
        }

        return acc;
      },
      { count: 0, newsletter: '' }
    );
  }, [mostReadNewsletters, newsletterNames]);

  // Capitalizes multi-word topics and phrases like 'ai' to 'AI'
  const capitalizedTopic = useMemo(() => {
    if (!mostReadTopic) return '';

    if (mostReadTopic.length <= 2) return mostReadTopic.toUpperCase();

    return capitalize(mostReadTopic);
  }, [mostReadTopic]);

  if (!articlesRead && !minutesRead && !mostReadTopic && !mostReadNewsletter?.newsletter) {
    return null;
  }

  return (
    <article className="items-between relative mt-48 flex flex-col justify-center gap-32 sm:flex-row sm:flex-wrap md:flex-nowrap lg:gap-64">
      {articlesRead && <MyTIReadingStat index={0} name="Articles Read" value={articlesRead} />}
      {minutesRead && (
        <MyTIReadingStat index={1} name="Reading Time" value={`${minutesRead} mins`} />
      )}
      {mostReadTopic && (
        <MyTIReadingStat index={2} name="Most-read Topic" value={capitalizedTopic} small />
      )}
      {mostReadNewsletter?.newsletter && (
        <MyTIReadingStat
          index={3}
          name="Most-read Newsletter"
          value={mostReadNewsletter.newsletter}
          small
        />
      )}
    </article>
  );
};

export default MyTIReadingStats;
